// Rekenwaarden en catalogus voor de systeemkeuze-calculator van Simply Offgrid.
// Elke waarde heeft een bron (commentaar erachter). Uitleg en werkwijze: systeemkeuze/README.md.
// Na elke wijziging: node systeemkeuze/test/run.js

const CONFIG = {
  versie: '2026-10-20',

  // Verbruik (helpdesk size-calculator: gemiddelde woning 7-9 kWh/dag)
  verbruik: {
    huishouden: { klein: 2200, gemiddeld: 3000, groot: 4500 }, // kWh/jaar zonder zware verbruikers
    // Extra jaarverbruik als de bezoeker geen eigen jaarverbruik opgeeft
    extraZwaar: { warmtepomp: 3000, laadpaal: 2500, boiler: 1500, inductie: 300, airco: 500 },
    nachtAandeel: 0.55, // deel van het dagverbruik in avond en nacht: grens voor de besparing (plan §3a)
    dekkingDynamisch: 1.0, // met een dynamisch contract dekt de accu een dag (laden als het goedkoop is, ook in de winter)
    maxDoelPraktijkKwh: 26, // plafond ≈ 32 kWh nominaal; groter levert per kWh minder op. Off-grid uitgezonderd
    offgridWoning: { tiny: 'klein', chalet: 'gemiddeld', woning: 'groot' }, // off-grid: vraag naar het type woning
  },

  // Accu's (datasheets, zie specs/accu-rekenwaarden-datasheets.md). ZYC niet in de calculator (Joris 20-10).
  accu: {
    praktijkFactor: 0.8, // nominaal → praktijk (min. SoC + omzetverlies)
    dodFabrikant: 0.95,
    merken: {
      fidus: { naam: 'Pylontech Fidus', kwhPerModule: 5.12, maxKwPerModule: 5.12, aanbevolenKwPerModule: 5.12, gewichtKg: 43 },
      dyness: { naam: 'Dyness PowerBrick Plus', kwhPerModule: 16.07, maxKwPerModule: 10.24, aanbevolenKwPerModule: 7.17, gewichtKg: 119 },
    },
    // Varianten in accu-set-ess (Shopify, 20-10). prijs = admin-prijs, alleen om goedkoopste te kiezen;
    // in het thema live uit Shopify. Fase: welke modules per fase te bestellen zijn.
    varianten: [
      { merk: 'fidus', modules: 1, kwh: 5.12, fase: ['1'], variantId: 52960857227601, prijs: 950.41, label: 'Pylontech Fidus / 5.12 kWh' },
      { merk: 'fidus', modules: 2, kwh: 10.24, fase: ['1'], variantId: 52960858046801, prijs: 1900.83, label: 'Pylontech Fidus / 10.24 kWh' },
      { merk: 'fidus', modules: 3, kwh: 15.36, fase: ['1'], variantId: 53045124890961, prijs: 2851.24, label: 'Pylontech Fidus / 15.36 kWh' },
      { merk: 'fidus', modules: 3, kwh: 15.36, fase: ['3'], variantId: 53142645145937, prijs: 2851.24, label: 'Pylontech Fidus / 15.36 kWh 3-fase' },
      { merk: 'fidus', modules: 6, kwh: 30.72, fase: ['3'], variantId: 53893826347345, prijs: 5702.48, label: 'Pylontech Fidus / 30.72 kWh 3-fase' },
      { merk: 'dyness', modules: 1, kwh: 16.07, fase: ['1', '3'], variantId: 52960858177873, prijs: 2024.79, label: 'Dyness PowerBrick Plus / 16.1 kWh' },
      { merk: 'dyness', modules: 2, kwh: 32.1, fase: ['1', '3'], variantId: 53045207859537, prijs: 3801.65, label: 'Dyness PowerBrick Plus / 32.1 kWh' },
      { merk: 'dyness', modules: 3, kwh: 48.2, fase: ['1', '3'], variantId: 53045217558865, prijs: 5578.51, label: 'Dyness PowerBrick Plus / 48.2 kWh' },
    ],
    voorkeurDynessVanafKwh: 15,
    tolerantie: 0.9, // een variant mag tot 10% onder het doel zitten (voorkomt een sprong van 16 naar 32 kWh) // helpdesk batteries.mdx: Dyness voordeliger vanaf ~15 kWh
    // Besloten 20-10: 1 Fidus bij een 6k5 mag (BMS regelt terug), met melding
    toegestaanOnderVermogen: [{ merk: 'fidus', modules: 1, omvormer: '1x6k5' }],
  },

  // Omvormers. acKw = continu bij 25 °C, va = nominaal (factor 1,0 met bestaande PV), doorvoerA = de
  // interne omschakelaar (de laatste waarde in de typenaam, bv. 48/4k5/55-32). Bron: Victron-typenamen en
  // dealerdatasheets (20-10); de 6k5 wordt soms ook met ~5,2 kW continu opgegeven.
  omvormers: {
    '1x3000': { label: 'MultiPlus-II 48/3000', fase: '1', acKw: 2.4, va: 3.0, doorvoerA: 32 },
    '1x4k5': { label: 'MultiPlus-II 48/4k5', fase: '1', acKw: 4.0, va: 4.5, doorvoerA: 32 },
    '1x6k5': { label: 'MultiPlus-II 48/6k5', fase: '1', acKw: 6.0, va: 6.5, doorvoerA: 50 },
    '3x3000': { label: '3× MultiPlus-II 48/3000', fase: '3', acKw: 7.2, va: 9.0, doorvoerA: 32 },
    '3x4k5': { label: '3× MultiPlus-II 48/4k5', fase: '3', acKw: 12.0, va: 13.5, doorvoerA: 32 },
    '3x6k5': { label: '3× MultiPlus-II 48/6k5', fase: '3', acKw: 18.0, va: 19.5, doorvoerA: 50 },
    'easy3000': { label: 'EasySolar-II GX 3000', fase: '1', acKw: 2.4, va: 3.0 },
    'easy4000': { label: 'EasySolar-II GX 5000 (4.000 W)', fase: '1', acKw: 4.0, va: 5.0 },
  },
  omvormerRendement: 0.93, // AC → DC-vraag aan de accu

  // Panelen (Joris 20-10: 500 Wp en 900 kWh/kWp)
  panelen: {
    wp: 500,
    opbrengstKwhPerKwp: 900,
    orientatie: { zuid: 1.0, 'oost-west': 0.85, noord: 0.7 },
    offgridKwhPerKwpPerDag: 2.5, // legacy-calculator 'najaar'; sales legt off-grid niet op de winter (testgevallen 24-26), winter via aggregaat
    offgridSysteemverlies: 0.85,
    offgridAutonomieDagen: 2,
  },

  // Sets en wat in Globo te kiezen is (Shopify aantal-zonnepanelen-in-sets en victron-cerbo-pro-configuratie, 20-10)
  sets: {
    'ess-1f': { naam: 'Victron Thuisbatterij 1-fase ESS', handle: 'victron-1-fase-ess-thuisbatterij-set', fase: '1', omvormers: ['1x4k5', '1x6k5', '1x3000'] },
    'ess-3f': { naam: 'Victron Thuisbatterij 3-fase ESS', handle: 'victron-3-fase-ess-thuisbatterij-set', fase: '3', omvormers: ['3x4k5', '3x6k5', '3x3000'] },
    'solar-easy': { naam: 'Victron Solar Easy', handle: 'victron-solar-easy', fase: '1', omvormers: ['easy3000', 'easy4000'], panelen: [4, 6, 8, 10, 12, 16] },
    'solar-pro-1f': { naam: 'Victron Solar Pro 1-fase', handle: 'victron-solar-pro-1-fase', fase: '1', omvormers: ['1x4k5', '1x6k5', '1x3000'], panelen: [4, 6, 8, 10, 12, 16] },
    'solar-pro-3f': { naam: 'Victron Solar Pro 3-fase', handle: 'victron-solar-pro-3-fase', fase: '3', omvormers: ['3x4k5', '3x6k5', '3x3000'], panelen: [8, 10, 12, 16, 30, 36] },
  },

  // Besparing (plan §3a). Ranges; elk jaar per 1 januari bijwerken. Nog te controleren vóór livegang.
  besparing: {
    cycliZelfverbruik: 200,
    cycliDynamisch: 300,
    NL: { waardeKwh: [0.22, 0.28], dynamischExtra: [0.10, 0.15], capaciteit: null },
    BE: { waardeKwh: [0.25, 0.30], dynamischExtra: [0.10, 0.15], capaciteit: { kwAfvlakking: [1, 2.5], euroPerKwJaar: [50, 60] } },
  },

  // Installatie (kennis/installatie/service/prijzen.mdx, okt 2026). Werkgebied als postcode-indicatie; sales bevestigt.
  installatie: {
    NL: {
      postcodes: [[1000, 3999]], // NH, ZH, Utrecht (grof; Flevoland en randen vallen erin)
      prijzen: {
        'ess-1f': 1450, 'ess-3f': 2625,
        'solar-1f': [{ tot: 10, prijs: 2450 }, { tot: 18, prijs: 3250 }],
        'solar-3f': [{ tot: 10, prijs: 3750 }, { tot: 20, prijs: 4575 }, { tot: 36, prijs: 5250 }],
      },
    },
    BE: {
      postcodes: [[3500, 3999]], // provincie Limburg
      alleenEss: true,
      prijzen: { 'ess-1f': 1450, 'ess-3f': 2625 },
    },
  },

  zwareVerbruikers: ['warmtepomp', 'inductie', 'boiler', 'airco'], // laadpaal telt niet: hoort niet op de noodstroom

  // Wanneer de zwaardere 6k5: drie technische regels in engine.js (doorvoer, PV per fase, vermogen bij storing)
  zwaardereOmvormer: {
    standaardAmpere1f: 35, // als de bezoeker het niet weet: NL 1-fase is meestal 1x35A of 1x40A
    standaardAmpere3f: 25, // NL 3-fase is meestal 3x25A
    pvEenFaseTot: 5, // PV-omvormer zonder opgave: tot 5 kW meestal 1-fase, daarboven 3-fase
    zwareVerbruikersVanaf3f: 3, // 3-fase: 4 kW per fase draagt één warmtepomp of boiler
  },
};

if (typeof module !== 'undefined') module.exports = { CONFIG };
else window.SOSysteemkeuzeConfig = CONFIG;
